import React, { FC, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';
import type { MuiTelInputCountry } from 'mui-tel-input';

import { useGetOrganizationBySlug, UserIdentifier } from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import ErrorAccessDenied from '@/components/ErrorAccessDenied/ErrorAccessDenied';
import ErrorGeneric from '@/components/ErrorGeneric/ErrorGeneric';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useOrganizationPermissions } from '@/hooks/permissions/useOrganizationPermissions';
import { PermissionKeys } from '@/hooks/permissions/usePermissions';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import typography from '@/theme/typography';
import countries from '@/utils/countries';
import ClientUserEmailDropdownSelect from '@/views/fields/UserDropdownSelect/ClientUserEmailDropdownSelect';
import InviteAdminModal from '@/views/Users/InviteAdminModal';

import { getInvitationLanguageOptions } from './basicOptions';

const RolesUsersTab: FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [prefilledEmail, setPrefilledEmail] = useState('');
  const { setValue, getValues, watch } = useFormContext();
  const formCountries = watch('countries');

  const defaultCountry = useMemo(() => {
    const firstCountry = Array.isArray(formCountries) ? formCountries[0] : formCountries;
    if (!firstCountry) return undefined;

    // Convert alpha-3 country code to alpha-2 for phone input
    return countries.alpha3ToAlpha2(firstCountry) as MuiTelInputCountry | undefined;
  }, [formCountries]);

  const { value: { canUpdateRolesAndUsers } = {} } = useOrganizationPermissions({
    variables: { organizationSlug }
  });

  const { data: organization } = useGetOrganizationBySlug(
    organizationSlug,
    {},
    { query: { enabled: !!organizationSlug } }
  );

  const handleNewEmailCreated = useCallback((email: string) => {
    setPrefilledEmail(email);
    setShowInviteModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowInviteModal(false);
    setPrefilledEmail('');
  }, []);

  const handleInviteAdmin = useCallback(
    (data: { email: string; phone: string }) => {
      // Replace the email-only entry (added by dropdown) with complete entry including phone
      // eslint-disable-next-line i18next/no-literal-string
      const currentAdmins = (getValues('admins') || []) as UserIdentifier[];
      const newAdmin: UserIdentifier = { email: data.email, phone: data.phone };
      // Filter out any existing entry with just the email (no phone), then add the complete one
      const filteredAdmins = currentAdmins.filter(admin => admin.email !== data.email);
      setValue('admins', [...filteredAdmins, newAdmin], { shouldDirty: true });
      handleCloseModal();
    },
    [getValues, setValue, handleCloseModal]
  );

  return (
    <PermissionGuard
      all={[PermissionKeys.OrganizationcanViewRolesAndUsers]}
      variables={{ organizationSlug }}
      accessDeniedFallback={<ErrorAccessDenied />}
      errorFallback={<ErrorGeneric />}
      loadingFallback={<BackdropLoader />}
    >
      <ReadonlyProvider readonly={!canUpdateRolesAndUsers}>
        <Stack
          width="100%"
          sx={{ '& .MuiFormLabel-root': { textAlign: 'start' }, '& .MuiFormControl-root': { pt: 0, px: 0 } }}
        >
          <Stack
            component={Typography}
            direction="row"
            justifyContent="space-between"
            width="100%"
            p={3}
            fontWeight={typography.fontWeightMedium}
          >
            {t('orgSettings.tabs.rolesAndUsers')}
          </Stack>
          <Stack direction="row" mx={3} gap={2}>
            <Field
              component={ClientUserEmailDropdownSelect}
              name="admins"
              label={t('orgSettings.fields.organizationAdmins')}
              withFormControl
              outerSx={{ width: '100%', flex: 1 }}
              innerProps={{
                usePortal: true,
                organizationId: organization?.id,
                onNewEmailCreated: handleNewEmailCreated
              }}
            />
            <Field
              component={DropdownSelect}
              name="userInviteEmailLanguage"
              label={t('orgSettings.fields.invitationLanguage')}
              innerProps={{
                options: getInvitationLanguageOptions(t),
                isMulti: false,
                usePortal: true,
                sx: {
                  minWidth: 320
                }
              }}
              withFormControl
            />
          </Stack>
        </Stack>
      </ReadonlyProvider>
      <InviteAdminModal
        isOpen={showInviteModal}
        onClose={handleCloseModal}
        onSubmit={handleInviteAdmin}
        prefilledEmail={prefilledEmail}
        defaultCountry={defaultCountry}
      />
    </PermissionGuard>
  );
};

export default RolesUsersTab;
